import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faExclamationTriangle, faEnvelope, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../api/api';
import { routes } from '../../config/utility';

const AccountDeletionRequest = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        email: '',
        reason: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            const response = await api.post('/account/request-deletion', formData);

            if (response.data.success) {
                toast.success('Deletion request sent! Please check your email for confirmation.');
                navigate(routes.login, {
                    state: {
                        message: 'Please check your email to confirm account deletion request.'
                    }
                });
            } else {
                toast.error(response.data.message || 'Failed to send deletion request');
            }
        } catch (error) {
            console.error('Deletion request error:', error);
            toast.error(error.response?.data?.message || 'Failed to send deletion request. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const cardVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.6, ease: "easeOut" }
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gradientStart to-gradientEnd flex items-center justify-center p-2 sm:p-4">
            <motion.div
                className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl p-4 sm:p-6 lg:p-8 max-w-sm sm:max-w-lg w-full max-h-[90vh] overflow-y-auto"
                variants={cardVariants}
                initial="hidden"
                animate="visible"
            >
                {/* Header */}
                <div className="text-center mb-6 sm:mb-8">
                    <div className="bg-red-100 rounded-full w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center mx-auto mb-3 sm:mb-4">
                        <FontAwesomeIcon icon={faTrash} className="text-red-500 text-xl sm:text-2xl" />
                    </div>
                    <h1 className="text-xl sm:text-2xl font-bold text-dark mb-2">Delete Account</h1>
                    <p className="text-body text-xs sm:text-sm">
                        Request to permanently delete your PawDash account
                    </p>
                </div>

                {/* Warning */}
                <div className="bg-red-50 border border-red-200 rounded-xl sm:rounded-2xl p-3 sm:p-4 mb-4 sm:mb-6">
                    <div className="flex items-start gap-2 sm:gap-3">
                        <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-500 mt-0.5 sm:mt-1 text-sm sm:text-base" />
                        <div>
                            <h3 className="font-semibold text-red-800 mb-1 text-sm sm:text-base">Important Notice</h3>
                            <p className="text-red-700 text-xs sm:text-sm leading-relaxed">
                                This action cannot be undone. All your data, pets, walks, and delivery history will be permanently deleted.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                    {/* Email */}
                    <div>
                        <label className="block text-sm font-medium text-dark mb-1 sm:mb-2">
                            Email Address
                        </label>
                        <div className="relative">
                            <FontAwesomeIcon
                                icon={faEnvelope}
                                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm sm:text-base"
                            />
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                required
                                className="w-full pl-8 sm:pl-10 pr-3 sm:pr-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent text-sm sm:text-base"
                                placeholder="Enter your email address"
                            />
                        </div>
                    </div>

                    {/* Reason */}
                    <div>
                        <label className="block text-sm font-medium text-dark mb-1 sm:mb-2">
                            Reason for Deletion (Optional)
                        </label>
                        <textarea
                            name="reason"
                            value={formData.reason}
                            onChange={handleInputChange}
                            rows={3}
                            maxLength={500}
                            className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent resize-none text-sm sm:text-base"
                            placeholder="Help us improve by sharing why you're leaving..."
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            {formData.reason.length}/500 characters
                        </p>
                    </div>

                    {/* Buttons */}
                    <div className="flex flex-col gap-2 sm:gap-3">
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="bg-red-500 hover:bg-red-600 disabled:bg-red-300 text-white py-2.5 sm:py-3 rounded-lg sm:rounded-xl font-semibold transition-colors duration-200 flex items-center justify-center gap-2 text-sm sm:text-base"
                        >
                            {isSubmitting ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    <span className="hidden sm:inline">Sending Request...</span>
                                    <span className="sm:hidden">Sending...</span>
                                </>
                            ) : (
                                <>
                                    <FontAwesomeIcon icon={faTrash} />
                                    <span className="hidden sm:inline">Request Account Deletion</span>
                                    <span className="sm:hidden">Request Deletion</span>
                                </>
                            )}
                        </button>

                        <button
                            type="button"
                            onClick={() => navigate(routes.login)}
                            className="bg-gray-500 hover:bg-gray-600 text-white py-2.5 sm:py-3 rounded-lg sm:rounded-xl font-semibold transition-colors duration-200 flex items-center justify-center gap-2 text-sm sm:text-base"
                        >
                            <FontAwesomeIcon icon={faArrowLeft} />
                            Back to Login
                        </button>
                    </div>
                </form>

                {/* Footer */}
                <div className="mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-gray-200 text-center">
                    <p className="text-xs text-gray-500 leading-relaxed">
                        Need help? Contact our support team
                    </p>
                </div>
            </motion.div>
        </div>
    );
};

export default AccountDeletionRequest;
